import React, { useState, useEffect } from 'react';
import {
  SUPPORT_CATEGORIES,
  type SupportCategory,
  type SupportRequest,
  type SupportTargetFile,
} from '../types/support';
import { createSupportRequest } from '../utils/supportSync';

interface RaiseHandModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetFile: SupportTargetFile | null;
  currentUser: { name?: string; email?: string } | null;
  onRequestCreated: (req: SupportRequest) => void;
}

export const RaiseHandModal: React.FC<RaiseHandModalProps> = ({
  isOpen,
  onClose,
  targetFile,
  currentUser,
  onRequestCreated,
}) => {
  const [userName, setUserName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<SupportCategory>(SUPPORT_CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Initialize form with current user info whenever modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultName =
        currentUser?.name ||
        (currentUser?.email ? currentUser.email.split('@')[0].replace('.', ' ') : '') ||
        '';
      setUserName(defaultName);
      setSelectedCategory(SUPPORT_CATEGORIES[0]);
      setDescription('');
      setSuccessMessage(null);
      setErrorMessage(null);
      setIsSubmitting(false);
    }
  }, [isOpen, currentUser]);

  if (!isOpen || !targetFile) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      setErrorMessage('Please describe your issue or request.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const created = await createSupportRequest({
        userName: userName.trim() || currentUser?.name || 'Intranet User',
        userEmail: (currentUser?.email || 'user@ssdspc.com').toLowerCase().trim(),
        fileId: targetFile.fileId,
        fileName: targetFile.fileName,
        fileUrl: targetFile.fileUrl,
        fileCategory: targetFile.categoryOrTeam || 'General',
        sourceColumn: targetFile.sourceColumn,
        sourceColumnLabel: targetFile.columnLabel,
        issueCategory: selectedCategory.name,
        assignedTo: selectedCategory.name,
        assignedEmail: 'adminhelp@ssdspc.com',
        description: description.trim(),
      });

      onRequestCreated(created);
      setSuccessMessage(`Support request submitted under "${selectedCategory.name}" successfully!`);

      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full p-6 space-y-5 shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100">
        {/* Modal Header */}
        <div className="flex justify-between items-start border-b border-gray-200 dark:border-gray-700 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl p-2 bg-amber-100 dark:bg-amber-950/60 rounded-xl text-amber-600 dark:text-amber-400">
              ✋
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                Raise Hand / Report Issue
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Submit an issue, request a change, or ask for help regarding this file.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-lg font-bold p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Selected File Banner */}
        <div className="p-3 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-xl space-y-1">
          <div className="flex items-center justify-between text-[11px] font-bold text-blue-700 dark:text-blue-300">
            <span className="uppercase tracking-wider">{targetFile.columnLabel}</span>
            {targetFile.categoryOrTeam && (
              <span className="bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full">
                {targetFile.categoryOrTeam}
              </span>
            )}
          </div>
          <div className="font-bold text-sm text-gray-900 dark:text-white truncate">
            {targetFile.fileName}
          </div>
          {targetFile.fileUrl && (
            <a
              href={targetFile.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-0.5 truncate"
            >
              <span>🔗</span> Open original document
            </a>
          )}
        </div>

        {/* Feedback Messages */}
        {successMessage && (
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
            <span>✓</span> {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 rounded-xl text-xs font-bold text-red-800 dark:text-red-300 flex items-center gap-2">
            <span>⚠️</span> {errorMessage}
          </div>
        )}

        {/* Support Request Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Your Name */}
          <div>
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Your full name"
              required
              className="w-full px-3.5 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl text-xs font-semibold text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition-all shadow-sm"
            />
            {currentUser?.email && (
              <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                <span>✉️</span> Submitting as: <span className="font-mono">{currentUser.email}</span>
              </p>
            )}
          </div>

          {/* Issue Category */}
          <div>
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
              Issue Category / Type of Request
            </label>
            <div className="relative">
              <select
                value={selectedCategory.id}
                onChange={(e) => {
                  const match = SUPPORT_CATEGORIES.find((c) => c.id === e.target.value);
                  if (match) setSelectedCategory(match);
                }}
                className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl text-xs font-semibold text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition-all shadow-sm cursor-pointer appearance-none pr-8"
              >
                {SUPPORT_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 text-xs">
                ▼
              </div>
            </div>
            {/* Category Description Hint Card */}
            <div className="mt-2 p-2.5 bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/50 rounded-xl text-[11px] text-amber-900 dark:text-amber-200">
              <div className="leading-snug">
                <span className="font-bold">{selectedCategory.name}: </span>
                <span className="text-gray-700 dark:text-gray-300">{selectedCategory.description}</span>
              </div>
            </div>
          </div>

          {/* Describe your Request */}
          <div>
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
              Describe the Issue / Request
            </label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Provide details regarding this ${selectedCategory.name.toLowerCase()}...`}
              required
              className="w-full px-3.5 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition-all shadow-sm resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end items-center gap-2.5 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-gray-950 text-xs font-bold rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">⏳</span> Submitting...
                </>
              ) : (
                <>
                  <span>✋</span> Submit Support Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

